/**
 * 차트 숫자 형식 (축·툴팁·표가 같은 함수를 쓴다). 화면은 이름(ValueFormat)이나 함수를 넘긴다.
 * 빈 값(null)은 "미수집" 등으로 보이고 0 으로 바꾸지 않는다.
 */
export type ValueFormat = "count" | "compact" | "percent" | "seconds" | "usd" | "rank" | ((n: number | null) => string);

const plain = new Intl.NumberFormat("ko-KR", { maximumFractionDigits: 1 });
const short = new Intl.NumberFormat("ko-KR", { notation: "compact", maximumFractionDigits: 1 });

export function num(n: number | null | undefined, missing = "미수집"): string {
  return n == null ? missing : plain.format(n);
}
/** 1.2만 · 3.4억 */
export function compact(n: number | null | undefined, missing = "미수집"): string {
  if (n == null) return missing;
  return Math.abs(n) < 10_000 ? plain.format(n) : short.format(n);
}
/** 45초 · 3분 20초 · 1시간 5분 */
export function duration(sec: number | null | undefined, missing = "기록 없음"): string {
  if (sec == null) return missing;
  const s = Math.round(sec);
  if (s < 60) return `${s}초`;
  if (s < 3600) return `${Math.floor(s / 60)}분${s % 60 ? ` ${s % 60}초` : ""}`;
  const m = Math.floor((s % 3600) / 60);
  return `${Math.floor(s / 3600)}시간${m ? ` ${m}분` : ""}`;
}
export function usd(n: number | null | undefined, missing = "—"): string {
  if (n == null) return missing;
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: n !== 0 && Math.abs(n) < 1 ? 4 : 2, maximumFractionDigits: Math.abs(n) < 1 ? 4 : 2 })}`;
}

/** 값 형식 → (툴팁·표용, 축용) 함수 */
export function formatterOf(f: ValueFormat = "count"): { value: (n: number | null) => string; axis: (n: number) => string } {
  if (typeof f === "function") return { value: f, axis: (n) => f(n) };
  switch (f) {
    case "compact":
      return { value: (n) => compact(n), axis: (n) => compact(n) };
    case "percent":
      return { value: (n) => (n == null ? "미수집" : `${plain.format(n)}%`), axis: (n) => `${plain.format(n)}%` };
    case "seconds":
      return { value: (n) => duration(n), axis: (n) => duration(n) };
    case "usd":
      return { value: (n) => usd(n), axis: (n) => usd(n) };
    case "rank":
      return { value: (n) => (n == null ? "미수집" : `${plain.format(n)}위`), axis: (n) => `${plain.format(n)}위` };
    default:
      return { value: (n) => num(n), axis: (n) => compact(n) };
  }
}
